import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import api, { formatPrice } from "../lib/api";
import { Plus } from "lucide-react";

const blank = { name: "", description: "", duration_minutes: 30, price: 0, walk_in_only: false, reminder_channels: ["email","sms"], active: true, buffer_minutes: 0, eligible_package_type_ids: [] };

export default function Services() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(blank);

  const load = () => api.get("/services", { params: { only_active: false } }).then(({ data }) => setServices(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing("new"); setDraft({ ...blank, price: "0.00" }); };
  const startEdit = (s) => {
    setEditing(s.id);
    setDraft({ ...blank, ...s, price: Number(s.price || 0).toFixed(2) });
  };

  const save = async () => {
    const dollars = parseFloat(draft.price);
    const payload = {
      ...draft,
      price: Number.isFinite(dollars) ? Math.round(dollars * 100) / 100 : 0,
      duration_minutes: Number(draft.duration_minutes),
    };
    if (editing === "new") await api.post("/services", payload);
    else await api.put(`/services/${editing}`, payload);
    setEditing(null); load();
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Services</p>
          <h1 className="font-display text-4xl text-charcoal">Service catalog</h1>
        </div>
        <button onClick={startNew} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md inline-flex items-center gap-1.5" data-testid="add-service">
          <Plus className="w-4 h-4" /> Add service
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-hairline rounded-lg shadow-warm p-6 mb-6" data-testid="service-editor">
          <h2 className="font-ui font-semibold text-charcoal mb-4">{editing === "new" ? "New service" : "Edit service"}</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name"><input className={inputCls} value={draft.name} onChange={(e) => setDraft({...draft, name: e.target.value})} data-testid="svc-name" /></Field>
            <Field label="Duration (minutes)"><input type="number" className={inputCls} value={draft.duration_minutes} onChange={(e) => setDraft({...draft, duration_minutes: e.target.value})} data-testid="svc-duration" /></Field>
            <Field label="Description" className="col-span-2"><textarea rows={2} className={inputCls} value={draft.description} onChange={(e) => setDraft({...draft, description: e.target.value})} data-testid="svc-description" /></Field>
            <Field label="Price (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate font-ui text-sm pointer-events-none">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  className={`${inputCls} pl-7`}
                  value={draft.price}
                  onChange={(e) => setDraft({...draft, price: e.target.value})}
                  placeholder="0.00"
                  data-testid="svc-price"
                />
              </div>
            </Field>
            <Field label="Walk-in only">
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={draft.walk_in_only} onChange={(e) => setDraft({...draft, walk_in_only: e.target.checked})} className="accent-sage" data-testid="svc-walkin" />
                <span className="text-sm font-ui text-slate">Excluded from online booking</span>
              </label>
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-ui text-slate hover:text-forest">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md" data-testid="svc-save">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="services-list">
        {services.map((s) => (
          <div key={s.id} className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="font-ui text-sm text-charcoal">{s.name} {s.walk_in_only && <span className="ml-2 text-xs font-ui text-forest bg-cream px-2 py-0.5 rounded-pill">Walk-in only</span>}</p>
              <p className="text-xs font-ui text-slate">{s.duration_minutes} min · {formatPrice(s.price)}</p>
            </div>
            <button onClick={() => startEdit(s)} className="text-xs font-ui text-sage hover:text-forest" data-testid={`edit-svc-${s.id}`}>Edit</button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";
function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-ui text-slate mb-1">{label}</span>
      {children}
    </label>
  );
}
